/**
 * iter175 — Email verification landing page.
 *
 * The verification link in the email hits `GET /api/auth/verify-email/{token}`
 * which redirects to `/verify-email/success` or `/verify-email/failed`.
 * This page reads the URL suffix and shows the appropriate message.
 */
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import DotboxLogo from '@/components/common/DotboxLogo';

const VerifyEmailPage = () => {
  const { status } = useParams();
  const [searchParams] = useSearchParams();
  const alreadyVerified = searchParams.get('already') === '1';
  // iter250 — set by the backend when the account came through the activation
  // gate: it has no session yet, so it must be sent to sign-in, not /dashboard.
  const activated = searchParams.get('activated') === '1';
  const navigate = useNavigate();
  const isSuccess = status === 'success';
  const isFailed = status === 'failed';
  const isPending = !isSuccess && !isFailed;

  return (
    <div className="min-h-screen bg-[#0A1024] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <DotboxLogo className="mx-auto h-10" variant="dark" />
        </div>
        <Card data-testid="verify-email-page">
          <CardContent className="p-8 text-center">
            {isPending && (
              <>
                <Loader2 className="h-12 w-12 text-slate-400 mx-auto mb-4 animate-spin" />
                <h1 className="text-xl font-bold text-white mb-1">Verifying…</h1>
              </>
            )}
            {isSuccess && (
              <>
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-emerald-400" />
                </div>
                <h1
                  className="text-xl font-bold text-white mb-1"
                  data-testid="verify-email-success-title"
                >
                  {alreadyVerified ? 'Already verified' : 'Email verified!'}
                </h1>
                <p className="text-slate-400 text-sm mb-6">
                  {alreadyVerified
                    ? 'Your email was already confirmed. Sign in to pick up where you left off.'
                    : activated
                      ? 'Your account is active and your 7-day trial starts now. Sign in to get going.'
                      : 'Thanks for confirming — all Signals and AI features are now unlocked.'}
                </p>
                <Button
                  onClick={() => navigate(activated || alreadyVerified ? '/login' : '/dashboard')}
                  className="w-full bg-[#DC2626] hover:bg-[#B91C1C]"
                  data-testid="verify-email-continue-btn"
                >
                  {activated || alreadyVerified ? 'Sign in' : 'Continue to Dashboard'}
                </Button>
              </>
            )}
            {isFailed && (
              <>
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-rose-500/20 flex items-center justify-center">
                  <XCircle className="h-10 w-10 text-rose-400" />
                </div>
                <h1
                  className="text-xl font-bold text-white mb-1"
                  data-testid="verify-email-failed-title"
                >
                  Verification link expired
                </h1>
                <p className="text-slate-400 text-sm mb-6">
                  This link is invalid or has expired. Request a fresh one — it only takes a second.
                </p>
                <Button
                  onClick={() => navigate('/activate')}
                  className="w-full bg-[#DC2626] hover:bg-[#B91C1C]"
                  data-testid="verify-email-back-to-login"
                >
                  Send a new link
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
